/* global SWDLocalise, Weglot */

const init = () => {
    // ---- CONFIG ----
    const countryLanguageMap = SWDLocalise.countries;
    const defaultCountry = 'om';

    // ---- COOKIES ----
    const setCountryCookie = (countryCode) => {
        const date = new Date();
        date.setTime(date.getTime() + 30 * 24 * 60 * 60 * 1000);
        document.cookie = `selected_country=${countryCode}; path=/; expires=${date.toUTCString()}`;
    };
    const getCountryFromCookie = () => {
        const m = document.cookie.match(/(?:^|;\s*)selected_country=([^;]+)/);
        return m ? m[1] : defaultCountry;
    };

    // ---- LANGUAGE HELPERS ----
    const LANG_SLUG = '[a-z]{2,3}(?:-[a-z0-9]+)*';
    const langFromPathRegex = new RegExp(`^\\/(${LANG_SLUG})(\\/|$)`, 'i');
    const stripLangFromPathRegex = new RegExp(`^\\/${LANG_SLUG}(\\/|$)`, 'i');

    const normalizeLang = (code) => (code || 'en').toLowerCase();
    const toLangSlug = (code) => normalizeLang(code).replace(/^wg-/, '');
    const isEnglishVariant = (langCode) => normalizeLang(langCode).startsWith('en');

    const getLangFromSwitcherLink = (anchor) => {
        // Prefer the EXTERNAL code. Weglot stores it in data-l (e.g. "es-pr"),
        // while data-code-language holds the INTERNAL code (e.g. "es-419").
        // Our ACF language codes and URLs use the external code, so match on it.
        const scope = anchor.closest('[data-l], [data-code-language]');
        const fromExternal = scope?.getAttribute('data-l');
        if (fromExternal) return toLangSlug(fromExternal);

        const fromData = scope?.getAttribute('data-code-language');
        if (fromData) return toLangSlug(fromData);

        const href = anchor.getAttribute('href') || '';

        const weglotHash = href.match(/#Weglot-([a-z0-9-]+)/i);
        if (weglotHash) return normalizeLang(weglotHash[1]);

        const weglotFake = href.match(/change-language\.weglot\.com\/([a-z0-9-]+)/i);
        if (weglotFake) return normalizeLang(weglotFake[1]);

        try {
            const { pathname } = new URL(href, window.location.origin);
            const m = pathname.match(langFromPathRegex);
            if (m) return normalizeLang(m[1]);
        } catch {
            const m = href.match(langFromPathRegex);
            if (m) return normalizeLang(m[1]);
        }

        return 'en';
    };

    const getCurrentLanguageFromPath = () => {
        const m = window.location.pathname.match(langFromPathRegex);
        return m ? normalizeLang(m[1]) : 'en';
    };

    const redirectToLang = (lang) => {
        const langNorm = normalizeLang(lang);
        const { pathname, search, hash } = window.location;
        const pathWithoutSlug = pathname.replace(stripLangFromPathRegex, '/');
        const newPath = isEnglishVariant(langNorm) ? pathWithoutSlug : `/${langNorm}${pathWithoutSlug}`;
        if (newPath !== pathname) {
            window.location.href = newPath + search + hash;
        }
    };

    // ---- COUNTRY META (NEW) ----
    // Accepts array form or object form with languages + default/defaultLang
    const getCountryMeta = (countryCode) => {
        const cfg = countryLanguageMap?.[countryCode];
        if (Array.isArray(cfg)) {
            const langs = cfg.map(normalizeLang);
            return {
                allowedLangs: langs.length ? langs : ['en'],
                defaultLang: langs[0] || 'en',
            };
        }
        if (cfg && typeof cfg === 'object') {
            const langs = Array.isArray(cfg.languages) ? cfg.languages.map(normalizeLang) : ['en'];
            const def = normalizeLang(cfg.default ?? cfg.defaultLang ?? langs[0] ?? 'en');
            return {
                allowedLangs: langs.length ? langs : ['en'],
                defaultLang: def,
            };
        }
        // Fallback
        return { allowedLangs: ['en'], defaultLang: 'en' };
    };

    // ---- WEGLOT UI HELPER ----
    // Weglot keeps its INTERNAL code (es-419) in the switcher DOM's
    // data-code-language and wg-* class, even though the external code (URL +
    // hreflang) is es-pr. So we identify this item by es-419 and relabel its
    // visible text to "ES". (data-l carries the external es-pr — see above.)
    const ES_419_LANG = 'es-419';
    const ES_419_LABEL = 'ES';

    const isEs419Lang = (code) => toLangSlug(code) === ES_419_LANG;

    const setEs419Label = (el) => {
        if (el.textContent !== ES_419_LABEL) {
            el.textContent = ES_419_LABEL;
        }
    };

    const updateLanguageLabels = (root = document.querySelector('.weglot-container')) => {
        if (!root) return;

        root.querySelectorAll(
            '[data-code-language="es-419"] a, [data-code-language="wg-es-419"] a, [data-l="es-pr"] a, .wg-es-419 a',
        ).forEach(setEs419Label);

        root.querySelectorAll(
            '[data-code-language="wg-es-419"] .wglanguage-name, [data-code-language="wg-es-419"] > span, .wglanguage-name, .wgcurrent span',
        ).forEach((el) => {
            const code = el.closest('[data-code-language]')?.getAttribute('data-code-language');
            if (isEs419Lang(code)) {
                setEs419Label(el);
            }
        });
    };

    let weglotLabelSyncInit = false;
    let weglotEventsBound = false;

    const bindWeglotLabelEvents = () => {
        if (weglotEventsBound || typeof Weglot === 'undefined') return weglotEventsBound;

        Weglot.on('languageChanged', updateLanguageLabels);
        Weglot.on('switchersReady', updateLanguageLabels);
        weglotEventsBound = true;
        return true;
    };

    const initWeglotLabelSync = () => {
        updateLanguageLabels();

        if (weglotLabelSyncInit) return;
        weglotLabelSyncInit = true;

        if (!bindWeglotLabelEvents()) {
            const interval = setInterval(() => {
                if (bindWeglotLabelEvents()) clearInterval(interval);
            }, 100);
            setTimeout(() => clearInterval(interval), 10000);
        }
    };

    const updateLanguageSwitcher = (allowedLangs) => {
        const container = document.querySelector('.weglot-container');
        if (container) container.style.display = allowedLangs.length <= 1 ? 'none' : '';

        let isSyncing = false;

        const syncSwitcher = () => {
            if (isSyncing) return;

            const root = document.querySelector('.weglot-container');
            if (!root) return;

            isSyncing = true;
            try {
                root.querySelectorAll('a').forEach((a) => {
                    let code = getLangFromSwitcherLink(a);
                    if (isEnglishVariant(code)) code = 'en';
                    const display = allowedLangs.includes(code) ? '' : 'none';
                    if (a.style.display !== display) {
                        a.style.display = display;
                    }
                });
                updateLanguageLabels(root);
            } finally {
                isSyncing = false;
            }
        };

        syncSwitcher();

        let syncTimer;
        const observer = new MutationObserver(() => {
            clearTimeout(syncTimer);
            syncTimer = setTimeout(syncSwitcher, 50);
        });
        observer.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => {
            clearTimeout(syncTimer);
            observer.disconnect();
        }, 5000);
    };

    // ---- MAIN ----
    document.addEventListener('DOMContentLoaded', () => {
        const country = getCountryFromCookie().toLowerCase();

        // ⬇️ Use country defaults
        const { allowedLangs, defaultLang } = getCountryMeta(country);
        const currentLang = getCurrentLanguageFromPath();

        // Dropdown toggle + focus trap (unchanged from your last version)
        const toggleBtn = document.getElementById('country-dropdown-toggle');
        const menu = document.getElementById('country-dropdown-menu');
        const drawer = document.getElementById('country-select-drawer');

        let focusableItems = [];

        const openMenu = () => {
            menu.removeAttribute('hidden');
            menu.setAttribute('aria-hidden', 'false');
            toggleBtn.setAttribute('aria-expanded', 'true');
            focusableItems = Array.from(menu.querySelectorAll('button, a')).filter((el) => !el.hasAttribute('disabled'));
            focusableItems[0]?.focus();
        };

        const closeMenu = () => {
            menu.setAttribute('hidden', '');
            menu.setAttribute('aria-hidden', 'true');
            toggleBtn.setAttribute('aria-expanded', 'false');
            toggleBtn.focus();
        };

        toggleBtn?.addEventListener('click', () => {
            if (menu.hasAttribute('hidden')) { openMenu(); return; }
            closeMenu();
        });

        document.addEventListener('keydown', (e) => {
            if (menu.hasAttribute('hidden')) return;
            if (e.key === 'Escape') { e.preventDefault(); closeMenu(); return; }
            if (e.key === 'Tab' && focusableItems.length > 0) {
                const firstItem = focusableItems[0];
                const lastItem = focusableItems[focusableItems.length - 1];
                if (e.shiftKey && document.activeElement === firstItem) {
                    e.preventDefault();
                    lastItem.focus();
                } else if (!e.shiftKey && document.activeElement === lastItem) {
                    e.preventDefault();
                    firstItem.focus();
                }
            }
        });

        // Internal switching via data-country buttons
        const attachCountrySwitchHandlers = (container) => {
            container?.querySelectorAll('button[data-country]').forEach((btn) => {
                btn.addEventListener('click', (event) => {
                    const selectedCountry = event.currentTarget.dataset.country.toLowerCase();
                    setCountryCookie(selectedCountry);

                    // ⬇️ Resolve allowed + default for the selected country
                    const {
                        allowedLangs: newAllowedLangs,
                        defaultLang: newDefaultLang,
                    } = getCountryMeta(selectedCountry);

                    updateLanguageSwitcher(newAllowedLangs);

                    const currentLangNow = getCurrentLanguageFromPath();

                    // If current language isn't allowed, go to that country's DEFAULT language
                    if (!newAllowedLangs.includes(currentLangNow)) {
                        redirectToLang(newDefaultLang);
                        return;
                    }

                    // Special-case English: strip slug if default is English
                    if (isEnglishVariant(currentLangNow)) {
                        if (/^\/en(?:-[a-z]{2})?(\/|$)/i.test(window.location.pathname)) {
                            redirectToLang('en');
                            return;
                        }

                        window.location.reload();
                        return;
                    }

                    redirectToLang(newDefaultLang);
                });
            });
        };

        attachCountrySwitchHandlers(menu);
        attachCountrySwitchHandlers(drawer);

        const selector = document.getElementById('country-selector');
        if (selector) selector.style.display = 'block';

        // If current language isn't allowed for the cookie country, redirect
        // to that country's DEFAULT language
        if (!allowedLangs.includes(currentLang)) {
            redirectToLang(defaultLang);
            return;
        }

        updateLanguageSwitcher(allowedLangs);
        initWeglotLabelSync();

        // Clean English slugs for any English variant
        if (isEnglishVariant(currentLang)) {
            document.querySelectorAll('a[href^="/en"], a[href*="/en-"]').forEach((link) => {
                link.href = link.href
                    .replace(/\/en(?:-[a-z]{2})?\//i, '/')
                    .replace(/\/en(?:-[a-z]{2})?$/i, '/');
            });
        }
    });
};

export default init;
